<template>
  <div class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
    <div class="bg-white rounded-lg shadow-xl max-w-6xl w-full mx-4 max-h-[90vh] flex flex-col">
      <!-- Header -->
      <div class="flex justify-between items-center p-6 border-b border-gray-200">
        <div>
          <h3 class="text-xl font-bold text-gray-900">{{ bank.name }}</h3>
          <p class="text-sm text-gray-600">{{ bank.description }}</p>
        </div>
        <button @click="$emit('close')" class="text-gray-400 hover:text-gray-600">
          <X class="w-6 h-6" />
        </button>
      </div>

      <!-- Tabs -->
      <div class="border-b border-gray-200">
        <nav class="flex -mb-px">
          <button
            @click="activeTab = 'overview'"
            :class="[
              'px-6 py-3 text-sm font-medium border-b-2 transition-colors',
              activeTab === 'overview'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            ]"
          >
            Overview
          </button>
          <button
            @click="activeTab = 'leave-types'"
            :class="[
              'px-6 py-3 text-sm font-medium border-b-2 transition-colors',
              activeTab === 'leave-types'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            ]"
          >
            Leave Types
          </button>
          <button
            @click="activeTab = 'employee-balances'"
            :class="[
              'px-6 py-3 text-sm font-medium border-b-2 transition-colors',
              activeTab === 'employee-balances'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            ]"
          >
            Employee Balances
          </button>
        </nav>
      </div>

      <!-- Content -->
      <div class="flex-1 overflow-y-auto p-6">
        <!-- Overview Tab -->
        <div v-if="activeTab === 'overview'" class="space-y-6">
          <!-- Bank Info -->
          <div class="grid grid-cols-2 gap-4">
            <div>
              <label class="text-sm text-gray-500">Bank Code</label>
              <p class="font-medium">{{ bank.code }}</p>
            </div>
            <div>
              <label class="text-sm text-gray-500">Calculation Type</label>
              <p class="font-medium">{{ formatCalculationType(bank.calculation_type) }}</p>
            </div>
            <div v-if="bank.fixed_days">
              <label class="text-sm text-gray-500">Fixed Days</label>
              <p class="font-medium">{{ bank.fixed_days }} days</p>
            </div>
            <div>
              <label class="text-sm text-gray-500">Carryover</label>
              <p class="font-medium">
                {{ bank.carryover_enabled ? `Yes (max ${bank.max_carryover || 'unlimited'} days)` : 'No' }}
              </p>
            </div>
          </div>

          <!-- Statistics -->
          <div class="border-t border-gray-200 pt-6">
            <h4 class="text-lg font-medium text-gray-900 mb-4">Statistics</h4>
            <div class="grid grid-cols-4 gap-4">
              <div class="text-center bg-blue-50 rounded-lg p-4">
                <p class="text-2xl font-bold text-blue-900">{{ bank.employee_count || 0 }}</p>
                <p class="text-xs text-blue-600">Employees</p>
              </div>
              <div class="text-center bg-green-50 rounded-lg p-4">
                <p class="text-2xl font-bold text-green-900">{{ Math.round(bank.total_allocated || 0) }}</p>
                <p class="text-xs text-green-600">Total Allocated</p>
              </div>
              <div class="text-center bg-orange-50 rounded-lg p-4">
                <p class="text-2xl font-bold text-orange-900">{{ Math.round(bank.total_used || 0) }}</p>
                <p class="text-xs text-orange-600">Total Used</p>
              </div>
              <div class="text-center bg-purple-50 rounded-lg p-4">
                <p class="text-2xl font-bold text-purple-900">{{ Math.round(bank.total_remaining || 0) }}</p>
                <p class="text-xs text-purple-600">Remaining</p>
              </div>
            </div>
          </div>
        </div>

        <!-- Leave Types Tab -->
        <div v-if="activeTab === 'leave-types'">
          <h4 class="text-lg font-medium text-gray-900 mb-4">Leave Types in this Bank</h4>
          <div class="space-y-2">
            <div v-if="loading" class="text-center py-4 text-gray-500">Loading...</div>
            <div v-else-if="leaveTypes.length === 0" class="text-center py-4 text-gray-500">
              No leave types assigned
            </div>
            <div v-else v-for="lt in leaveTypes" :key="lt.id" class="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
              <div class="w-3 h-3 rounded-full" :style="{ backgroundColor: lt.color }"></div>
              <span class="font-medium">{{ lt.name }}</span>
              <span class="px-2 py-1 text-xs bg-gray-200 rounded">{{ lt.code }}</span>
            </div>
          </div>
        </div>

        <!-- Employee Balances Tab -->
        <div v-if="activeTab === 'employee-balances'" class="space-y-4">
          <!-- Filters -->
          <div class="flex gap-4 items-center">
            <div class="flex-1">
              <input
                v-model="searchQuery"
                type="text"
                placeholder="Search employees..."
                class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <select
                v-model="selectedYear"
                @change="loadEmployeeBalances"
                class="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option v-for="year in availableYears" :key="year" :value="year">{{ year }}</option>
              </select>
            </div>
            <button
              @click="loadEmployeeBalances"
              class="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
            >
              <RefreshCw class="w-4 h-4" />
              Refresh
            </button>
          </div>

          <!-- Employee Balances Table -->
          <div class="border border-gray-200 rounded-lg overflow-hidden">
            <div v-if="loadingBalances" class="p-8 text-center">
              <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
              <p class="text-sm text-gray-500 mt-2">Loading employee balances...</p>
            </div>

            <div v-else-if="filteredEmployeeBalances.length === 0" class="p-8 text-center text-gray-500">
              <Users class="w-12 h-12 mx-auto mb-2 text-gray-300" />
              <p>No employees found</p>
            </div>

            <table v-else class="min-w-full divide-y divide-gray-200">
              <thead class="bg-gray-50">
                <tr>
                  <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Employee</th>
                  <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Department</th>
                  <th class="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Allocated</th>
                  <th class="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Used</th>
                  <th class="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Pending</th>
                  <th class="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Remaining</th>
                  <th class="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody class="bg-white divide-y divide-gray-200">
                <tr v-for="balance in filteredEmployeeBalances" :key="balance.id" class="hover:bg-gray-50">
                  <td class="px-4 py-3">
                    <div class="font-medium text-gray-900">{{ balance.employee_name }}</div>
                    <div class="text-xs text-gray-500">{{ balance.employee_code }}</div>
                  </td>
                  <td class="px-4 py-3 text-sm text-gray-600">{{ balance.department_name || 'N/A' }}</td>
                  <td class="px-4 py-3 text-right">
                    <span class="font-medium text-gray-900">{{ balance.allocated_days }}</span>
                    <span v-if="balance.is_overridden" class="ml-1 text-xs text-orange-600" title="Manually adjusted">*</span>
                  </td>
                  <td class="px-4 py-3 text-right text-gray-600">{{ balance.used_days }}</td>
                  <td class="px-4 py-3 text-right text-gray-600">{{ balance.pending_days }}</td>
                  <td class="px-4 py-3 text-right">
                    <span :class="getRemainingClass(balance.remaining_days)" class="font-medium">
                      {{ balance.remaining_days }}
                    </span>
                  </td>
                  <td class="px-4 py-3 text-center">
                    <button
                      @click="openEditDialog(balance)"
                      class="text-blue-600 hover:text-blue-900 text-sm font-medium"
                    >
                      <Edit class="w-4 h-4 inline" />
                      Adjust
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <!-- Footer -->
      <div class="flex justify-end gap-3 p-6 border-t border-gray-200">
        <button
          @click="$emit('close')"
          class="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
        >
          Close
        </button>
      </div>
    </div>

    <!-- Edit Balance Dialog -->
    <div v-if="editingBalance" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]">
      <div class="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
        <h4 class="text-lg font-bold text-gray-900 mb-4">Adjust Balance - {{ editingBalance.employee_name }}</h4>

        <div class="space-y-4">
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2">Current Allocated Days</label>
            <p class="text-2xl font-bold text-gray-900">{{ editingBalance.allocated_days }}</p>
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2">New Allocated Days *</label>
            <input
              v-model.number="editForm.allocated_days"
              type="number"
              min="0"
              step="0.5"
              required
              class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2">Reason for Adjustment *</label>
            <textarea
              v-model="editForm.reason"
              rows="3"
              required
              placeholder="Please provide a reason for this manual adjustment..."
              class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            ></textarea>
          </div>

          <div class="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
            <p class="text-sm text-yellow-800">
              <strong>Note:</strong> This will override the automatic calculation for this employee. The change will be recorded in the audit history.
            </p>
          </div>
        </div>

        <div class="flex justify-end gap-3 mt-6">
          <button
            @click="closeEditDialog"
            class="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
          >
            Cancel
          </button>
          <button
            @click="saveBalanceAdjustment"
            :disabled="saving || editForm.allocated_days === null || editForm.allocated_days === undefined || !editForm.reason"
            class="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {{ saving ? 'Saving...' : 'Save Adjustment' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed, watch } from 'vue';
import { X, RefreshCw, Users, Edit } from 'lucide-vue-next';
import balanceBankService from '@/services/balanceBankService';

const props = defineProps({
  bank: {
    type: Object,
    required: true
  }
});

const emit = defineEmits(['close', 'refresh']);

// State
const activeTab = ref('overview');
const loading = ref(false);
const loadingBalances = ref(false);
const saving = ref(false);
const leaveTypes = ref([]);
const employeeBalances = ref([]);
const searchQuery = ref('');
const selectedYear = ref(new Date().getFullYear());
const editingBalance = ref(null);
const editForm = ref({
  allocated_days: 0,
  reason: ''
});

// Available years (current year + 2 past years + 1 future year)
const availableYears = computed(() => {
  const currentYear = new Date().getFullYear();
  return [currentYear - 2, currentYear - 1, currentYear, currentYear + 1];
});

// Filtered employee balances based on search
const filteredEmployeeBalances = computed(() => {
  if (!searchQuery.value) return employeeBalances.value;

  const query = searchQuery.value.toLowerCase();
  return employeeBalances.value.filter(balance =>
    balance.employee_name?.toLowerCase().includes(query) ||
    balance.employee_code?.toLowerCase().includes(query) ||
    balance.department_name?.toLowerCase().includes(query)
  );
});

// Watch for tab change to load data when needed
watch(activeTab, (newTab) => {
  if (newTab === 'employee-balances' && employeeBalances.value.length === 0) {
    loadEmployeeBalances();
  }
});

// Load leave types
onMounted(async () => {
  loading.value = true;
  try {
    const response = await balanceBankService.getBankLeaveTypes(props.bank.id);
    // Handle response - ensure we get an array
    leaveTypes.value = Array.isArray(response.data)
      ? response.data
      : (response.data?.data || []);
    console.log('Leave types loaded:', leaveTypes.value);
  } catch (error) {
    console.error('Error loading leave types:', error);
    leaveTypes.value = [];
  } finally {
    loading.value = false;
  }
});

// Load employee balances
async function loadEmployeeBalances() {
  loadingBalances.value = true;
  try {
    console.log('Loading employee balances for bank:', props.bank.id, 'year:', selectedYear.value);
    const response = await balanceBankService.getAllEmployeeBankBalances({
      bank_id: props.bank.id,
      year: selectedYear.value
    });
    console.log('Employee balances response:', response);
    // Handle response - ensure we get an array
    const data = response.data?.data || response.data || [];
    employeeBalances.value = Array.isArray(data) ? data : [];
    console.log('Loaded employee balances:', employeeBalances.value.length);
  } catch (error) {
    console.error('Error loading employee balances:', error);
    console.error('Error details:', error.response?.data || error.message);
    employeeBalances.value = [];
    alert('Failed to load employee balances: ' + (error.response?.data?.message || error.message));
  } finally {
    loadingBalances.value = false;
  }
}

// Open edit dialog
function openEditDialog(balance) {
  editingBalance.value = balance;
  editForm.value = {
    allocated_days: balance.allocated_days,
    reason: ''
  };
}

// Close edit dialog
function closeEditDialog() {
  editingBalance.value = null;
  editForm.value = {
    allocated_days: 0,
    reason: ''
  };
}

// Save balance adjustment
async function saveBalanceAdjustment() {
  if (editForm.value.allocated_days === null || editForm.value.allocated_days === undefined || !editForm.value.reason) {
    alert('Please fill in all required fields');
    return;
  }

  saving.value = true;
  try {
    await balanceBankService.overrideEmployeeBalance(
      editingBalance.value.employee_id,
      props.bank.id,
      {
        year: selectedYear.value,
        allocated_days: editForm.value.allocated_days,
        reason: editForm.value.reason
      }
    );

    console.log('Balance adjustment saved successfully');

    // Reload balances
    await loadEmployeeBalances();

    // Emit refresh to update parent
    emit('refresh');

    // Close dialog
    closeEditDialog();

    alert('Balance adjusted successfully!');
  } catch (error) {
    console.error('Error saving balance adjustment:', error);
    alert('Failed to save balance adjustment. Please try again.');
  } finally {
    saving.value = false;
  }
}

// Helper functions
function formatCalculationType(type) {
  const labels = {
    tenure_based: 'Tenure-Based',
    fixed: 'Fixed',
    manual: 'Manual'
  };
  return labels[type] || type;
}

function getRemainingClass(remaining) {
  if (remaining < 0) return 'text-red-600';
  if (remaining < 5) return 'text-orange-600';
  return 'text-green-600';
}
</script>
